import React from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  Link,
  Paper,
  Stack,
} from "@mui/material";

const LAST_UPDATED = "May 7, 2026";
const CONTACT_EMAIL = "support@mysharestock.org";
const COMPANY_NAME = "ShareStock";
const GOVERNING_LAW = "the State of California, United States";

const READING_WIDTH = 720;

const listSx = {
  pl: 3,
  mt: 1,
  mb: 0,
  "& li": { mb: 1, lineHeight: 1.75 },
  "& li:last-of-type": { mb: 0 },
};

const bodySx = { lineHeight: 1.75 };

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box component="section" sx={{ mb: 5 }}>
    <Typography
      variant="h5"
      component="h2"
      sx={{ fontWeight: 600, mb: 2 }}
    >
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" sx={bodySx}>
    {children}
  </Typography>
);

export const TermsPage = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 6, mb: 8, textAlign: "left" }}>
      <Box sx={{ maxWidth: READING_WIDTH, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "left",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              Terms of Service
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {LAST_UPDATED}
            </Typography>
          </Box>

          <Divider sx={{ mb: 5 }} />

          <Section title="1. Acceptance of Terms">
            <Body>
              These Terms of Service ("Terms") govern your access to and use
              of {COMPANY_NAME} ("we," "us," or "our"), the consumer
              marketplace operated as a Shopify sales channel and at
              mysharestock.org. By installing the {COMPANY_NAME} app on
              your Shopify store, signing up for an account on our
              marketplace, or using any part of the service, you agree to be
              bound by these Terms. If you do not agree, do not use the
              service.
            </Body>
          </Section>

          <Section title="2. Definitions">
            <Box component="ul" sx={listSx}>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Service</strong> — the {COMPANY_NAME} marketplace,
                the {COMPANY_NAME} Shopify sales channel app, and all
                related features.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Merchant</strong> — a Shopify store operator who has
                installed the {COMPANY_NAME} app and lists products on the
                marketplace.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Buyer</strong> — a shopper who purchases products
                listed on the marketplace through Shopify Checkout on the
                Merchant's store.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                <strong>Listing</strong> — a product a Merchant has
                published to the {COMPANY_NAME} sales channel.
              </Typography>
            </Box>
          </Section>

          <Section title="3. Eligibility and Accounts">
            <Body>
              To use the Service as a Merchant, you must operate a Shopify
              store on an active plan and have authority to bind the
              business that owns it. To use the Service as a Buyer, you
              must be at least 16 years old and provide accurate
              registration information. You are responsible for safeguarding
              your account credentials and for all activity under your
              account.
            </Body>
          </Section>

          <Section title="4. Use of the Marketplace">
            <Body>
              {COMPANY_NAME} is a venue that connects Merchants and Buyers.
              We do not take title to products listed on the marketplace,
              do not process payments outside Shopify Checkout, and do not
              warehouse, ship, or fulfill orders. All purchases are
              completed through the Merchant's Shopify Checkout under the
              Merchant's standard terms.
            </Body>
          </Section>

          <Section title="5. Merchant Obligations">
            <Box component="ul" sx={listSx}>
              <Typography component="li" variant="body1" sx={bodySx}>
                Only list products you have the legal right to sell.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Maintain accurate inventory levels, pricing, and product
                details.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Fulfill orders through your Shopify store's standard
                fulfillment process.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Comply with all applicable laws, including
                consumer-protection, tax, and data-protection laws, in the
                jurisdictions where you sell.
              </Typography>
            </Box>
          </Section>

          <Section title="6. Buyer Obligations">
            <Box component="ul" sx={listSx}>
              <Typography component="li" variant="body1" sx={bodySx}>
                Provide accurate billing and shipping information.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Honor commitments to Merchants you purchase from,
                including payment, returns, and dispute-resolution
                processes set by the Merchant.
              </Typography>
            </Box>
          </Section>

          <Section title="7. Pricing and Fees">
            <Body>
              The {COMPANY_NAME} sales channel app is provided to Merchants
              free of charge. We do not charge listing fees, monthly
              subscription fees, or commissions on transactions. Buyers
              transact directly with Merchants through Shopify Checkout;
              standard Shopify transaction fees set by the Merchant's
              Shopify plan apply.
            </Body>
          </Section>

          <Section title="8. Intellectual Property">
            <Body>
              You retain all rights to content you submit (product titles,
              descriptions, images). By listing on {COMPANY_NAME}, you grant
              us a non-exclusive, worldwide, royalty-free license to
              display, reproduce, and distribute that content for the
              purpose of operating the Service. {COMPANY_NAME}'s software,
              branding, and marketplace design are owned by us and protected
              by copyright, trademark, and other laws.
            </Body>
          </Section>

          <Section title="9. Prohibited Conduct">
            <Box component="ul" sx={listSx}>
              <Typography component="li" variant="body1" sx={bodySx}>
                Listing counterfeit, stolen, or unauthorized goods.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Manipulating reviews, ratings, or marketplace search
                results.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Attempting to bypass Shopify Checkout or settle transactions
                off-platform.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Scraping, reverse engineering, or interfering with the
                Service.
              </Typography>
              <Typography component="li" variant="body1" sx={bodySx}>
                Uploading malware, infringing content, or content that
                violates applicable law.
              </Typography>
            </Box>
          </Section>

          <Section title="10. Termination">
            <Body>
              You may stop using the Service at any time by uninstalling the{" "}
              {COMPANY_NAME} app from your Shopify Admin or by closing your
              marketplace account. We may suspend or terminate access for
              any Merchant or Buyer who violates these Terms or whose use of
              the Service creates risk for the platform or other users.
              Upon termination, listings are removed from the marketplace;
              orders already placed continue to be processed by the
              Merchant under their store's standard terms.
            </Body>
          </Section>

          <Section title="11. Disclaimers">
            <Body>
              The Service is provided "as is" without warranties of any
              kind, express or implied. {COMPANY_NAME} does not warrant
              that the Service will be uninterrupted, error-free, or that
              any specific inventory will be available. We are not a party
              to transactions between Merchants and Buyers and disclaim
              responsibility for the quality, safety, legality, or accuracy
              of listings and the conduct of users.
            </Body>
          </Section>

          <Section title="12. Limitation of Liability">
            <Body>
              To the fullest extent permitted by law, {COMPANY_NAME} will
              not be liable for any indirect, incidental, consequential,
              special, or punitive damages, or any loss of profits or
              revenue, arising out of your use of the Service. Our total
              cumulative liability for any claim relating to the Service
              will not exceed one hundred U.S. dollars ($100).
            </Body>
          </Section>

          <Section title="13. Governing Law and Disputes">
            <Body>
              These Terms are governed by the laws of {GOVERNING_LAW},
              without regard to its conflict-of-law principles. Any dispute
              arising out of or relating to these Terms or the Service will
              be resolved exclusively in the state or federal courts
              located in that jurisdiction.
            </Body>
          </Section>

          <Section title="14. Changes to These Terms">
            <Body>
              We may update these Terms from time to time. If we make
              material changes, we will notify Merchants and Buyers through
              the Service or by email at least 14 days before the changes
              take effect. Your continued use of the Service after the
              effective date constitutes acceptance of the updated Terms.
            </Body>
          </Section>

          <Section title="15. Contact">
            <Body>
              Questions about these Terms?{" "}
              <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                {CONTACT_EMAIL}
              </Link>
            </Body>
          </Section>
        </Paper>
      </Box>
    </Container>
  );
};

export default TermsPage;
